import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/pos-api";
import { useAuth } from "@/context/AuthContext";
import {
  ScanLine, LayoutDashboard, ShoppingCart, Package, Tags, Users as UsersIcon,
  Receipt, LogOut, Store, ShoppingBag, Truck, Printer, FileBarChart, HandCoins, Boxes, Clock, DatabaseBackup, CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  {
    section: null,
    items: [
      { to: "/pos", label: "Kasir", icon: ShoppingCart, roles: ["admin", "cashier"] },
      { to: "/transactions", label: "Transaksi", icon: Receipt, roles: ["admin", "cashier"] },
      { to: "/piutang", label: "Piutang", icon: HandCoins, roles: ["admin", "cashier"] },
    ],
  },
  {
    section: "Backoffice",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"] },
      { to: "/reports", label: "Laporan", icon: FileBarChart, roles: ["admin"] },
      { to: "/shifts", label: "Riwayat Shift", icon: Clock, roles: ["admin"] },
      { to: "/purchases", label: "Pembelian", icon: ShoppingBag, roles: ["admin"] },
      { to: "/suppliers", label: "Supplier", icon: Truck, roles: ["admin"] },
      { to: "/products", label: "Produk & Stok", icon: Package, roles: ["admin"] },
      { to: "/stok", label: "Stok (Opname/Transfer)", icon: Boxes, roles: ["admin"] },
      { to: "/categories", label: "Kategori", icon: Tags, roles: ["admin"] },
      { to: "/users", label: "Pengguna", icon: UsersIcon, roles: ["admin"] },
      { to: "/stores", label: "Toko", icon: Store, roles: ["admin"] },
      { to: "/backup", label: "Backup & Restore", icon: DatabaseBackup, roles: ["admin"] },
      { to: "/settings", label: "Printer", icon: Printer, roles: ["admin"] },
      { to: "/payment-methods", label: "Metode Pembayaran", icon: CreditCard, roles: ["admin"] },
    ],
  },
];

export default function AppLayout() {
  const { user, logout, tenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navHidden, setNavHidden] = useState(false);
  useEffect(() => {
    setNavHidden(location.pathname === "/pos");
  }, [location.pathname]);
  const groups = NAV
    .map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(user.role)) }))
    .filter((g) => g.items.length > 0);

  const [masterTenants, setMasterTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(localStorage.getItem("pos_tenant_id") || "");
  useEffect(() => { if (tenant?.kind === "master") api.get("/master/tenants").then((r) => setMasterTenants(r.data)).catch(() => {}); }, [tenant?.kind]);
  const [stores, setStores] = useState([]);
  const [activeStore, setActiveStore] = useState(localStorage.getItem("pos_store") || "");
  useEffect(() => {
    api.get("/stores").then((r) => {
      setStores(r.data);
      if (!localStorage.getItem("pos_store")) {
        const def = user.role === "cashier" && user.store_id ? user.store_id : (r.data.find((s) => s.is_default)?.id || r.data[0]?.id);
        if (def) { localStorage.setItem("pos_store", def); setActiveStore(def); }
      }
    }).catch(() => {});
  }, []);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const [showLogout, setShowLogout] = useState(false);
  const lpTimer = useRef(null);
  const startLongPress = () => {
    clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => setShowLogout(true), 600);
  };
  const cancelLongPress = () => clearTimeout(lpTimer.current);

  return (
    <div className="h-screen flex bg-[#E8EBF0] text-slate-800 overflow-hidden">
      {/* Sidebar */}
      <aside className={`w-[84px] xl:w-64 bg-[#F7F9FB] border-r border-slate-200 flex flex-col shrink-0${navHidden ? " hidden" : ""}`}>
        <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-200">
          <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0">
            <ScanLine className="h-6 w-6 text-slate-950" />
          </div>
          <div className="hidden xl:block">
            <div className="font-heading font-extrabold leading-none">POS KASIR SM</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Point of Sale</div>
          </div>
        </div>

        <div className="hidden xl:block px-3 py-2 border-b border-slate-200">
          {tenant?.kind === "master" && <select data-testid="tenant-selector" value={selectedTenant} onChange={(e) => { const value=e.target.value; value ? localStorage.setItem("pos_tenant_id", value) : localStorage.removeItem("pos_tenant_id"); localStorage.removeItem("pos_store"); setSelectedTenant(value); window.location.reload(); }} className="w-full h-9 mb-2 rounded-lg bg-[#F7F9FB] border border-amber-500 text-xs text-slate-700 px-2"><option value="">Pilih tenant… (data kosong)</option>{masterTenants.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.host}</option>)}</select>}
          {user.role === "admin" ? (
            <select
              data-testid="store-selector"
              value={activeStore}
              onChange={(e) => { localStorage.setItem("pos_store", e.target.value); window.location.reload(); }}
              className="w-full h-9 rounded-lg bg-[#F7F9FB] border border-slate-300 text-xs text-slate-700 px-2"
            >
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          ) : (
            <div className="text-xs text-slate-500 truncate">Toko: <span className="text-slate-700 font-semibold">{stores.find((s) => s.id === user.store_id)?.name || "-"}</span></div>
          )}
        </div>

        <nav className="flex-1 p-2 xl:p-3 space-y-1 overflow-y-auto">
          {groups.map((g, gi) => (
            <div key={gi} className="space-y-1">
              {g.section && (
                <div className="hidden xl:block px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  {g.section}
                </div>
              )}
              {g.section && <div className="xl:hidden my-2 border-t border-slate-200" />}
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 h-12 transition-colors no-select ${
                      isActive
                        ? "bg-emerald-500 text-slate-950 font-bold"
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span className="hidden xl:inline text-sm">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-2 xl:p-3 border-t border-slate-200">
          <div
            className="hidden xl:flex items-center gap-3 px-3 py-2 mb-2 rounded-lg cursor-pointer select-none hover:bg-slate-50"
            title="Tekan &amp; tahan untuk menampilkan tombol Keluar"
            onPointerDown={startLongPress}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
          >
            <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[11px] text-emerald-600 uppercase tracking-wide">
                {user.role === "admin" ? "Administrator" : "Kasir"}
              </div>
            </div>
          </div>
          {showLogout ? (
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={doLogout}
              className="w-full justify-center xl:justify-start gap-2 text-slate-500 hover:text-red-600 hover:bg-red-500/10 h-11"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden xl:inline">Keluar</span>
            </Button>
          ) : (
            <button
              data-testid="logout-reveal"
              onPointerDown={startLongPress}
              onPointerUp={cancelLongPress}
              onPointerLeave={cancelLongPress}
              className="w-full flex items-center justify-center gap-2 text-slate-500 hover:text-slate-500 h-8 text-[11px]"
              title="Tekan &amp; tahan untuk keluar"
            >
              <LogOut className="h-4 w-4 xl:hidden" />
              <span className="hidden xl:inline">Tekan &amp; tahan untuk keluar</span>
            </button>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-hidden flex flex-col">
        <Outlet context={{ navHidden, setNavHidden }} />
      </main>
    </div>
  );
}
